import { readError } from './api';

export interface StudioPreview {
  id: string;
  status: 'processing' | 'completed' | 'failed';
  previewUrl: string | null;
  error: string | null;
}

interface StudioPreviewResponse {
  preview: StudioPreview;
}

export async function startStudioPreview(
  photo: Blob,
  requestKey: string,
  garment?: { name: string; category: string; primaryColor: string },
): Promise<StudioPreview> {
  const form = new FormData();
  form.set('photo', photo, 'garment.jpg');
  if (garment) {
    form.set('name', garment.name);
    form.set('category', garment.category);
    form.set('primaryColor', garment.primaryColor);
  }
  const response = await fetch('/api/ai/studio-preview', {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json', 'X-Preview-Key': requestKey },
    body: form,
  });
  if (!response.ok) throw new Error(await readError(response));
  return ((await response.json()) as StudioPreviewResponse).preview;
}

export async function getStudioPreview(id: string): Promise<StudioPreview> {
  const response = await fetch(`/api/ai/studio-preview/${encodeURIComponent(id)}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(await readError(response));
  return ((await response.json()) as StudioPreviewResponse).preview;
}

export async function waitForStudioPreview(
  id: string,
  timeoutMs = 90_000,
): Promise<StudioPreview> {
  const startedAt = Date.now();
  for (;;) {
    const preview = await getStudioPreview(id);
    if (preview.status !== 'processing') return preview;
    if (Date.now() - startedAt > timeoutMs) return preview;
    await new Promise((resolve) => setTimeout(resolve, 2_000));
  }
}

export async function fetchStudioPreviewImage(previewUrl: string): Promise<File> {
  const response = await fetch(previewUrl, { credentials: 'include', cache: 'no-store' });
  if (!response.ok) throw new Error('The clean garment image could not be loaded.');
  const blob = await response.blob();
  return new File([blob], 'clean-garment.png', { type: blob.type || 'image/png' });
}
